import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  Truck,
  RefreshCw,
  Package,
  DollarSign,
  Ruler,
  ChevronLeft,
  ChevronRight,
  Star,
  Plus,
  Minus,
  Shield,
} from 'lucide-react';
import ProductSlider from './ProductSlider';
import { products } from '../products';
import { clickedProduct } from '../firebaseConfig';
import Footer from './Footer';
import specifications from '../specification';
import { FaTshirt } from 'react-icons/fa';
import FooterSection from './landing/FooterSection';
import SEO from './SEO';

// Define colors as CSS variables for consistency
const colors = {
  gold: '#DBAE6E',
  black: '#000000',
  white: '#ffffff',
};

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const ProductDetail = ({ handleAddToCart, setAddingToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAdded, setIsAdded] = useState(false);
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);
  const [isSpecificationsOpen, setIsSpecificationsOpen] = useState(false);

  // Load product data
  useEffect(() => {
    const product = products.find((p) => p.id === parseInt(id));
    if (product) {
      clickedProduct(product.id);
      setSelectedProduct(product);
      setSelectedColor(product.colors?.[0] || '');
      setSelectedSize(product.sizes?.[0] || '');
      setQuantity(1);
      setCurrentImageIndex(0);
    } else {
      setSelectedProduct(null); // Handle invalid product ID
    }
  }, [id]);

  // Scroll to top on product change
  useEffect(() => {
    if (selectedProduct) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedProduct]);

  // Get images for selected color
  const images = selectedProduct?.images?.[selectedColor] || [];

  // Memoize total price
  const totalPrice = useMemo(() => {
    if (!selectedProduct) return '0.00';
    return (selectedProduct.price * (1 - (selectedProduct.discount || 0)) * quantity).toFixed(2);
  }, [selectedProduct, quantity]);

  // Get product specification
  const productSpec = useMemo(() => {
    return specifications.find((spec) => spec.id === selectedProduct?.id);
  }, [selectedProduct]);

  // Handlers
  const handleAddToCartClick = () => {
    if (!selectedProduct || !selectedColor || !selectedSize) return;
    setAddingToCart(selectedProduct.id);
    handleAddToCart(selectedProduct, selectedSize, selectedColor, quantity);
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 2000);
  };

  const handleBuyNow = () => {
    if (!selectedProduct || !selectedColor || !selectedSize) return;
    setAddingToCart(selectedProduct.id);
    handleAddToCart(selectedProduct, selectedSize, selectedColor, quantity);
    navigate('/cart');
  };

  const handleIncreaseQuantity = () => setQuantity((prev) => prev + 1);
  const handleDecreaseQuantity = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  const handleProductChange = (newProduct) => {
    setSelectedProduct(newProduct);
    setSelectedColor(newProduct.colors?.[0] || '');
    setSelectedSize(newProduct.sizes?.[0] || '');
    setQuantity(1);
    setCurrentImageIndex(0);
    setIsAdded(false);
    navigate(`/product/${newProduct.id}`);
  };

  // Loading state
  if (!selectedProduct) {
    return (
      <div className="text-black dark:text-white text-center py-28 bg-primary dark:bg-primary-dark min-h-screen">
        Loading...
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto bg-primary dark:bg-primary-dark min-h-screen p-4 mt-10">
      <SEO
        title={`${selectedProduct.name} - Laynered Store`}
        description={selectedProduct.description || `Buy the premium ${selectedProduct.name} at Laynered Store. High-quality fabric and perfect fit.`}
        keywords={`${selectedProduct.name.toLowerCase()}, custom t-shirt, layered t-shirt, premium t-shirt, buy ${selectedProduct.name.toLowerCase()} online, Laynered`}
        image={images[0] || "/logo.png"}
      />
      <style>
        {`
          :root {
            --gold: ${colors.gold};
            --black: ${colors.black};
            --white: ${colors.white};
          }
        `}
      </style>

      {/* Mobile Header */}
      <motion.div
        className="p-4 md:hidden"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <button
          onClick={() => navigate('/shop')}
          className="flex items-center gap-2 text-secondary hover:text-black dark:text-secondary transition-colors text-lg font-medium"
          aria-label="Go back to shop"
        >
          <ArrowLeft size={24} />
          Back
        </button>
        <h2 className="text-2xl font-bold text-black dark:text-white mt-2">
          {quantity > 1 ? `${selectedProduct.name} (x${quantity})` : selectedProduct.name}
        </h2>
      </motion.div>

      {/* Main Content */}
      <div className="md:flex gap-8">
        {/* Image Section */}
        <motion.div className="md:w-1/2" initial="hidden" animate="visible" variants={fadeIn}>
          <div className="relative w-full h-[400px] md:h-[600px] rounded-lg overflow-hidden shadow-lg mb-4 border border-[var(--gold)]/20">
            <motion.img
              key={images[currentImageIndex]}
              src={images[currentImageIndex] || '/placeholder-image.jpg'}
              alt={`${selectedProduct.name} - ${selectedColor}`}
              className="w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            />
            {images.length > 1 && (
              <>
                <motion.button
                  onClick={() =>
                    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
                  }
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-[var(--gold)]/80 text-black p-3 rounded-full shadow-md hover:bg-[var(--gold)] transition-all"
                  whileHover={{ scale: 1.1 }}
                  aria-label="Previous image"
                >
                  <ChevronLeft size={24} />
                </motion.button>
                <motion.button
                  onClick={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-[var(--gold)]/80 text-black p-3 rounded-full shadow-md hover:bg-[var(--gold)] transition-all"
                  whileHover={{ scale: 1.1 }}
                  aria-label="Next image"
                >
                  <ChevronRight size={24} />
                </motion.button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-5">
              {images.map((image, index) => (
                <motion.div
                  key={index}
                  className={`relative w-[50px] h-[50px] rounded-md overflow-hidden cursor-pointer border ${currentImageIndex === index ? 'border-[var(--gold)]' : 'border-transparent'
                    }`}
                  whileHover={{ scale: 1.05 }}
                  onClick={() => setCurrentImageIndex(index)}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              ))}
            </div>
          )}

        </motion.div>

        {/* Details Section */}
        <motion.div
          className="md:w-1/2 p-4 md:p-0 flex flex-col"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <div className="hidden md:block mb-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-secondary dark:text-secondary text-lg font-medium"
              aria-label="Go back to home"
            >
              <ArrowLeft size={24} />
              Back
            </button>
            <h2 className="text-3xl font-bold text-black dark:text-white mt-2">
              {quantity > 1 ? `${selectedProduct.name} (x${quantity})` : selectedProduct.name}
            </h2>
            <p className="text-[var(--gold)] mt-2">{selectedProduct.description}</p>
          </div>

          <p className="text-2xl md:text-3xl font-semibold text-black dark:text-white">
            ₹{totalPrice}
            {selectedProduct.discount > 0 && (
              <span className="text-lg md:text-xl text-[var(--gold)]/70 line-through ml-3">
                ₹{(selectedProduct.price * quantity).toFixed(2)}
              </span>
            )}
          </p>

          {/* Size Selection */}
          <div className="mt-6">
            <p className="font-semibold text-black dark:text-white text-lg mb-2">Size:</p>
            <div className="flex gap-3 flex-wrap">
              {selectedProduct.sizes?.map((size) => (
                <motion.button
                  key={size}
                  className={`w-14 h-14 border ${selectedSize === size
                    ? 'bg-[var(--gold)] text-black border-[var(--primary)]'
                    : 'bg-white text-black border-white dark:bg-[var(--black)] dark:text-white dark:border-[var(--primary)] hover:bg-[var(--gold)] hover:text-black'
                    } flex items-center justify-center font-medium transition-colors`}
                  onClick={() => setSelectedSize(size)}
                  whileHover={{ scale: 1.05 }}
                  aria-label={`Select size ${size}`}
                >
                  {size}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Quantity Selection */}
          <div className="mt-6">
            <p className="font-semibold text-black dark:text-white text-lg mb-2">Quantity:</p>
            <div className="flex items-center gap-3">
              <motion.button
                onClick={handleDecreaseQuantity}
                className="w-14 h-14 border border-white bg-white text-black dark:bg-[var(--black)] dark:text-white dark:border-[var(--primary)] hover:bg-[var(--gold)] hover:text-black flex items-center justify-center font-medium transition-colors"
                whileHover={{ scale: 1.05 }}
                disabled={quantity <= 1}
                aria-label="Decrease quantity"
              >
                <Minus size={24} />
              </motion.button>
              <div className="w-14 h-14 bg-[var(--gold)] text-black flex items-center justify-center font-medium border border-[var(--primary)]">
                {quantity}
              </div>
              <motion.button
                onClick={handleIncreaseQuantity}
                className="w-14 h-14 border border-white bg-white text-black dark:bg-[var(--black)] dark:text-white dark:border-[var(--primary)] hover:bg-[var(--gold)] hover:text-black flex items-center justify-center font-medium transition-colors"
                whileHover={{ scale: 1.05 }}
                aria-label="Increase quantity"
              >
                <Plus size={24} />
              </motion.button>
            </div>
          </div>



          {/* Size Chart */}
          <div className="mt-6">
            <button
              className="flex items-center gap-2 text-[var(--gold)] hover:text-black dark:text-white transition-colors"
              onClick={() => setIsSizeChartOpen(!isSizeChartOpen)}
              aria-label="Toggle size chart"
            >
              <Ruler size={20} />
              <span className="font-semibold">Size Chart</span>
            </button>
            {isSizeChartOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-4 overflow-hidden"
              >
                <table className="w-full text-sm text-black dark:text-white border-collapse border border-[var(--gold)]/30">
                  <thead>
                    <tr className="bg-[var(--gold)]/10">
                      <th className="p-2 border border-[var(--gold)]/30">Size</th>
                      <th className="p-2 border border-[var(--gold)]/30">Chest (in)</th>
                      <th className="p-2 border border-[var(--gold)]/30">Length (in)</th>
                      <th className="p-2 border border-[var(--gold)]/30">Shoulder (in)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProduct.sizeChart?.map((item) => (
                      <tr key={item.size} className="border-b border-[var(--gold)]/30">
                        <td className="p-2 border border-[var(--gold)]/30">{item.size}</td>
                        <td className="p-2 border border-[var(--gold)]/30">{item.chest}</td>
                        <td className="p-2 border border-[var(--gold)]/30">{item.length}</td>
                        <td className="p-2 border border-[var(--gold)]/30">{item.shoulder}</td>
                      </tr>
                    )) || (
                        <tr>
                          <td colSpan="4" className="p-2 text-center">
                            No size chart available
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </motion.div>
            )}
          </div>

          {/* Specifications */}
          <div className="mt-6">
            <button
              className="flex items-center gap-2 text-[var(--gold)] hover:text-black dark:text-white transition-colors"
              onClick={() => setIsSpecificationsOpen(!isSpecificationsOpen)}
              aria-label="Toggle specifications"
            >
              <FaTshirt size={20} />
              <span className="font-semibold">Specification</span>
            </button>
            {isSpecificationsOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-4 overflow-hidden"
              >
                <table className="w-full text-sm text-black dark:text-white border-collapse border border-[var(--gold)]/30">
                  <thead>
                    <tr className="bg-[var(--gold)]/10">
                      <th className="p-2 border border-[var(--gold)]/30">Attribute</th>
                      <th className="p-2 border border-[var(--gold)]/30">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productSpec ? (
                      [
                        { label: 'Material', value: productSpec.material },
                        { label: 'Fit', value: productSpec.fit },
                        { label: 'Care Instructions', value: productSpec.care },
                        { label: 'Manufacturer', value: productSpec.manufacturer },
                        { label: 'Origin', value: productSpec.origin },
                      ].map((spec, index) => (
                        <tr key={index} className="border-b border-[var(--gold)]/30">
                          <td className="p-2 border border-[var(--gold)]/30 font-semibold">
                            {spec.label}
                          </td>
                          <td className="p-2 border border-[var(--gold)]/30">{spec.value}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className="p-2 text-center">
                          No specifications available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </motion.div>
            )}
          </div>
          {/* Policies */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="flex items-center gap-2">
              <RefreshCw size={20} className="text-[var(--gold)]" />
              <p className="text-sm text-black dark:text-white">
                3 Days <span className="text-[var(--gold)]">Exchange</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Truck size={20} className="text-[var(--gold)]" />
              <p className="text-sm text-black dark:text-white">
                Free <span className="text-[var(--gold)]">Shipping</span>
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="flex items-center gap-2">
              <Shield size={20} className="text-[var(--gold)]" />
              <p className="text-sm text-black dark:text-white">
                Guarenteed <span className="text-[var(--gold)]">Quality</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <FaTshirt size={20} className="text-[var(--gold)]" />
              <p className="text-sm text-black dark:text-white">
                240 gsm <span className="text-[var(--gold)]">Thickness</span>
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex flex-col gap-4">
            <motion.button
              onClick={handleAddToCartClick}
              className="w-full py-3 bg-[var(--white)] text-black text-lg font-semibold border border-[var(--black)] hover:bg-[var(--gold)]/90 transition-colors"
              whileHover={{ scale: 1.02 }}
              disabled={!selectedColor || !selectedSize}
              aria-label={isAdded ? 'Added to cart' : 'Add to cart'}
            >
              {isAdded ? 'Added to Cart' : 'Add to Cart'}
            </motion.button>
            <motion.button
              onClick={handleBuyNow}
              className="w-full py-3 bg-[var(--gold)] text-black text-lg font-semibold border border-[var(--black)] hover:bg-[var(--gold)]/80 transition-colors"
              whileHover={{ scale: 1.02 }}
              disabled={!selectedColor || !selectedSize}
              aria-label="Buy now"
            >
              Buy Now
            </motion.button>
          </div>
          <Footer />

        </motion.div>
      </div>

      {/* Product Slider */}
      <motion.div className="p-4 md:p-8" initial="hidden" animate="visible" variants={fadeIn}>
        <ProductSlider
          products={products}
          onProductSelect={handleProductChange}
          selectedProductId={selectedProduct.id}
        />
      </motion.div>

      <FooterSection />
    </div>
  );
};

export default ProductDetail;